import { dueSummary, listEnrolled, type EnrolledCollection } from "../lib/store";
import { supa } from "../lib/supa";
import { formatShortDate, guessTimeZone } from "../lib/time";
import { lang, t } from "../lib/i18n";
import { clear, h, mount } from "../ui/dom";
import { navigate } from "../ui/router";
import { signedIn } from "../ui/app";
import { actionPlaceholder, collectionCard, progressPlaceholder, rowPlaceholder, startedPlaceholder, statsPlaceholder } from "./parts";
import { statsCard } from "./stats-card";

const SHAPE_KEY = "lexa.home.shape";

/** Last visit's list: how many rows, and how tall the whole block was. */
interface HomeShape {
  rows: number;
  height: number;
}

/**
 * What the list looked like last time, or null if we've never seen it.
 *
 * Deliberately only ever a shape we have actually observed. A guess would be
 * wrong for exactly the people it matters most to: a new account has nothing
 * enrolled, so guessing "about three" would flash three skeletons and then an
 * empty state — worse than the plain wait it replaced. First visit therefore
 * behaves as before; every visit after it is exact.
 *
 * The height is stored alongside the count because a row's height depends on
 * what's in it — a curated collection carries a Persian subtitle and a meta
 * line and runs ~100px, a bare user collection ~62px. No placeholder can guess
 * that per row, but reserving the block's total height means the page doesn't
 * resize as the real rows land.
 */
function rememberedShape(): HomeShape | null {
  try {
    const raw = localStorage.getItem(SHAPE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return null;
    const { rows, height } = parsed as Record<string, unknown>;
    if (typeof rows !== "number" || !Number.isInteger(rows) || rows <= 0) return null;
    if (typeof height !== "number" || !Number.isFinite(height) || height <= 0) return null;
    // Clamped so a corrupt value can't build thousands of nodes or reserve a
    // screen's worth of blank space.
    return { rows: Math.min(rows, 8), height: Math.min(height, 4000) };
  } catch {
    return null; // private mode, storage disabled, or unparseable
  }
}

function rememberShape(shape: HomeShape): void {
  try {
    localStorage.setItem(SHAPE_KEY, JSON.stringify(shape));
  } catch {
    // Nothing to do — the placeholder is an optimisation, not a requirement.
  }
}

export async function renderHome(host: HTMLElement): Promise<void> {
  if (!signedIn) {
    mount(
      host,
      h("div", { class: "screen stack" },
        h("h2", { text: t("tagline") }),
        h("p", { class: "muted", text: t("signInPitch") }),
        h("button", { class: "primary", text: t("signIn"), on: { click: () => navigate("/signin") } }),
        h("button", { class: "ghost", text: t("navBrowse"), on: { click: () => navigate("/browse") } }),
      ),
    );
    return;
  }

  // The shell goes up once and stays. Everything asynchronous swaps inside
  // `list`, so the heading never moves and there is no whole-screen remount
  // between the loading and loaded states. The stats card is unconditional —
  // it's about the learner, not about any one collection, so it renders even
  // before we know whether they have collections at all.
  const list = h("div", { class: "card-list" });
  mount(
    host,
    h("div", { class: "screen stack" }, h("h2", { text: t("navHome") }), statsCard(), list),
  );
  const shape = rememberedShape();
  if (shape) {
    list.style.minBlockSize = `${shape.height}px`;
    mount(list, ...Array.from({ length: shape.rows }, () => rowPlaceholder()));
  }

  const enrolled = await listEnrolled();

  if (!enrolled.length) {
    // Store rows:0 rather than leaving the old shape behind, so the next visit
    // reserves nothing instead of a gap where collections used to be.
    rememberShape({ rows: 0, height: 0 });
    list.style.minBlockSize = "";
    mount(
      list,
      h("p", { class: "muted", text: t("noCollections") }),
      h("button", { class: "primary", text: t("navBrowse"), on: { click: () => navigate("/browse") } }),
    );
    return;
  }

  // Counts come from dueSummary, which reuses loadStudySet — so the numbers can
  // never disagree with what a session actually serves. Rendered progressively
  // because each one is a few round trips; a single Promise.all would leave the
  // list blank until the slowest finished.
  clear(list);
  // For the "Mine" tag: Browse's own list gets this for free from listMine()
  // filtering by owner, but Home shows enrolled collections regardless of who
  // owns them, so telling the two apart here needs the viewer's own id.
  const { data } = await supa.auth.getSession();
  const uid = data.session?.user.id;
  for (const item of enrolled) {
    const row = collectionCard(item.collection, {
      onOpen: () => navigate(`/c/${item.collection.id}`),
      showProgress: true,
      showFooter: true,
      owned: !!uid && item.collection.owner_id === uid,
    });
    list.append(row);
    void attachCounts(row, item);
  }

  // Measure with the reservation released, or a too-generous remembered height
  // would measure itself and never converge. Row heights are already final here:
  // `.counts` has a floor, so the counts arriving later cannot change them.
  list.style.minBlockSize = "";
  rememberShape({ rows: enrolled.length, height: list.getBoundingClientRect().height });
}

async function attachCounts(row: HTMLElement, item: EnrolledCollection): Promise<void> {
  // `.counts-action` — not the whole `.counts` div — is what gets replaced
  // here: `.counts` may also hold a static delete button (`.counts-end`'s
  // other child), rendered once by `collectionCard` and never touched again.
  const startSlot = row.querySelector(".counts-start");
  const actionSlot = row.querySelector(".counts-action");
  const progressSlot = row.querySelector(".card-progress");
  const statsSlot = row.querySelector(".stats-dynamic");
  if (!startSlot || !actionSlot) return;
  // Runs before the first await, so the placeholder is in the DOM in the same
  // frame as the row itself — never a visible gap. Browse renders the same rows
  // and never fills them in, which is why this lives here and not in
  // `collectionCard`: a permanent skeleton there would be worse than none.
  mount(startSlot, startedPlaceholder());
  mount(actionSlot, actionPlaceholder());
  if (progressSlot) mount(progressSlot, progressPlaceholder());
  if (statsSlot) mount(statsSlot, ...statsPlaceholder());
  try {
    const summary = await dueSummary(item.collection.id, guessTimeZone());

    if (progressSlot && summary.total) {
      const pct = Math.round(((summary.total - summary.notStarted) / summary.total) * 100);
      mount(
        progressSlot,
        h("div", { class: "card-progress-head" },
          h("span", { class: "muted small", text: t("progress") }),
          h("span", { class: "small", text: t("percentValue", { n: pct }) }),
        ),
        h("div", { class: "bar" }, h("div", { class: "bar-fill", attrs: { style: `inline-size: ${pct}%` } })),
      );
    }

    // Numbers about the deck read as one line: the static card count plus
    // whatever's due/new, once known.
    if (statsSlot) {
      const pills: HTMLElement[] = [];
      if (summary.due) pills.push(h("span", { class: "pill due", text: t("dueToday", { n: summary.due }) }));
      if (summary.fresh) pills.push(h("span", { class: "pill fresh", text: t("newToday", { n: summary.fresh }) }));
      mount(statsSlot, ...pills);
    }

    // Plain textContent, not mount(): `.counts-start` only ever holds text,
    // unlike `.counts-action` which needs to hold a whole button.
    startSlot.textContent = t("startedOn", { when: formatShortDate(new Date(summary.addedAt), lang()) });

    if (!summary.due && !summary.fresh) {
      mount(actionSlot, h("span", { class: "muted small", text: t("sessionDone") }));
      return;
    }

    mount(
      actionSlot,
      h("button", {
        class: "contrast small",
        text: t("studyNow"),
        on: {
          click: (event) => {
            event.stopPropagation(); // the row itself navigates to the collection
            navigate(`/study/${item.collection.id}`);
          },
        },
      }),
    );
  } catch (error) {
    mount(actionSlot, h("span", { class: "muted small", text: error instanceof Error ? error.message : t("errGeneric") }));
  }
}
